import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Delete, Edit, Search as SearchIcon, WarningAmber } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import {
  createMRoyTest,
  deleteMRoyTest,
  fetchMRoyTests,
  updateMRoyTest,
} from "../../../api/m_roy";

const emptyForm = { name: "", description: "", price: "", is_active: true };

const MRoyTestsPage = () => {
  const { t } = useTranslation();
  const token = useAuth()?.user?.token;
  const [tests, setTests] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => setTests(await fetchMRoyTests(token));

  useEffect(() => {
    if (token) load();
  }, [token]);

  const filtered = useMemo(() => {
    return tests.filter((t) => {
      const q = query.toLowerCase();
      const matches = [t.name, t.description, String(t.price), t.is_active ? "active" : "inactive"]
        .join(" ")
        .toLowerCase()
        .includes(q);
      return matches;
    });
  }, [tests, query]);

  const save = async () => {
    if (!form.name || form.price === "") return;
    const payload = {
      ...form,
      price: Number(form.price),
    };
    try {
      if (editing?.id) {
        await updateMRoyTest(token, editing.id, payload);
        setSnackbar({ open: true, message: t("mri.mriTestUpdatedSuccessfully", "MRI test updated successfully!"), severity: "success" });
      } else {
        await createMRoyTest(token, payload);
        setSnackbar({ open: true, message: t("mri.mriTestAddedSuccessfully", "MRI test added successfully!"), severity: "success" });
      }
    } catch (e) {
      setSnackbar({ open: true, message: t("mri.failedToSaveMriTest", "Failed to save MRI test!"), severity: "error" });
    }
    setEditing(null);
    setForm(emptyForm);
    load();
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      description: row.description || "",
      price: row.price ?? "",
      is_active: !!row.is_active,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const toggleStatus = async () => {
    if (!editing?.id) return;
    try {
      await updateMRoyTest(token, editing.id, {
        ...form,
        price: Number(form.price),
        is_active: !form.is_active,
      });
      setSnackbar({ open: true, message: t("mri.mriTestStatusUpdatedSuccessfully", "MRI test status updated successfully!"), severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: t("mri.failedToUpdateMriTestStatus", "Failed to update MRI test status!"), severity: "error" });
    }
    setEditing(null);
    setForm(emptyForm);
    load();
  };

  const handleDeleteRequest = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await deleteMRoyTest(token, deleteId);
      setSnackbar({ open: true, message: t("mri.mriTestDeletedSuccessfully", "MRI test deleted successfully!"), severity: "success" });
      setDeleteDialogOpen(false);
      setDeleteId(null);
      load();
    } catch (e) {
      setSnackbar({ open: true, message: t("mri.failedToDeleteMriTest", "Failed to delete MRI test!"), severity: "error" });
    }
  };

  return (
    <Grid container sx={{ p: 2, maxWidth: 1024, mx: "auto" }}>
      <Grid item xs={12}>
        <Paper sx={{ width: "100%", p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {editing ? t("mri.editMriTest", "Edit MRI Test") : t("mri.addNewMriTest", "Add New MRI Test")}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t("mri.mriTestName", "MRI Test name")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t("mri.description", "Description")}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={4}
            />
            <TextField
              label={t("mri.costInAfghani", "Cost In (Afghani)")}
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={save}>
                {editing ? t("mri.updateTest", "Update Test") : t("mri.addTest", "Add Test")}
              </Button>
              {editing ? (
                <>
                  <Button variant="outlined" onClick={cancelEdit}>
                    {t("mri.cancel", "Cancel")}
                  </Button>
                  <Button variant="outlined" color="warning" onClick={toggleStatus}>
                    {form.is_active ? t("mri.markAsInactive", "Mark as Inactive") : t("mri.markAsActive", "Mark as Active")}
                  </Button>
                </>
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        <Paper sx={{ width: "100%", p: 3, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {t("mri.listOfMriTests", "List of MRI Tests")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={t("mri.search", "Search")}
            placeholder={t("mri.searchByTestNameOrDescription", "Search by test name or description")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, opacity: 0.7 }} />,
            }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t("mri.typeName", "Type Name")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t("mri.description", "Description")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t("mri.costInAfghani", "Cost In (Afghani)")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">{t("mri.actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.id} hover sx={{ backgroundColor: "#e0f4f7" }}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description || "-"}</TableCell>
                    <TableCell>{row.price}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => startEdit(row)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRequest(row.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Paper
          elevation={4}
          sx={{
            width: 315,
            borderRadius: "6px",
            bgcolor: "#e9ecef",
            overflow: "hidden",
            "@keyframes MRIToastProgressLtr": {
              from: { transform: "scaleX(1)" },
              to: { transform: "scaleX(0)" },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1.1 }}>
            <CheckCircleIcon
              sx={{
                color: snackbar.severity === "success" ? "#17b629" : "#d32f2f",
                mr: 1,
                fontSize: 21,
              }}
            />
            <Typography
              sx={{ color: "#5f6368", fontSize: "0.78rem", flexGrow: 1, lineHeight: 1.2 }}
            >
              {snackbar.message}
            </Typography>
            <CloseIcon
              onClick={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              sx={{ color: "#8f8f8f", cursor: "pointer", fontSize: 18 }}
            />
          </Box>
          <Box
            sx={{
              height: 4,
              bgcolor: snackbar.severity === "success" ? "#22c841" : "#d32f2f",
              width: "100%",
              transformOrigin: "left",
              animation: "MRIToastProgressLtr 3s linear forwards",
            }}
          />
        </Paper>
      </Snackbar>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <WarningAmber sx={{ fontSize: 84, color: "#d32f2f", mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            {t("mri.confirmDeleting", "Confirm Deleting")}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t("mri.deleteWarning", "Are you sure you want to delete this item? This action cannot be undone.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteId(null);
            }}
            sx={{ bgcolor: "#0f6074", "&:hover": { bgcolor: "#0b4f5f" } }}
          >
            {t("mri.cancel", "Cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            {t("mri.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default MRoyTestsPage;


